import axios from 'axios';
import io from 'socket.io-client';

export const api = process.env.REACT_APP_API_URL;

let token = localStorage.getItem('token');
let subs = {};
export let socket;

let setAuthenticated = () => {};
let pushAlert, socketStatus;

initSocket();

export function setAuthenticatedCallback(cb) { setAuthenticated = cb; }
export function setPushAlert(cb) { pushAlert = cb; }
export function onSocketStatus(cb) { socketStatus = cb; }

const handleVisibilityChange = () => {
  if (document.visibilityState === 'hidden') closeSocket();
  else if (document.visibilityState === 'visible') initSocket();
};
document.addEventListener('visibilitychange', handleVisibilityChange);

export async function apiFetch(params) {
  return axios({
    url: `${api}${params.endpoint}`,
    method: params.method || 'GET',
    headers: Object.assign(
      { Accept: 'application/json', 'Content-Type': 'application/json;charset=UTF-8' },
      token ? { Authorization: `Bearer ${token}` } : null,
      params.headers
    ),
    data: params.data,
    params: params.params,
    signal: params.abortSignal,
    responseType: params.responseType || 'json',
  }).catch(e => {
    if (e.response?.status === 403) Logout();
    else throw e;
  });
}

const dataPromise = async (p, silent) =>
  new Promise((resolve, reject) =>
    p.then(r => (r && typeof r.data !== 'undefined' ? resolve(r.data) : reject(r))).catch(reject)
  ).catch(e => {
    if (!silent && pushAlert) {
      const message = e.response?.data?.errors;
      if (message) pushAlert({ severity: 'error', message, timestamp: new Date() });
    }
    throw e;
  });

async function initSocket() {
  await closeSocket();
  if (!token) return;
  socket = io(api, { auth: { token }, transports: ['websocket'] });
  socket.on('connect', () => socketStatus && socketStatus(true));
  socket.on('disconnect', () => socketStatus && socketStatus(false));
  socket.on('sub', data => {
    const mcb = d => console.log('Missing callback for subscription', d);
    if (!(data.coll in subs)) return mcb(data);
    if ('type' in data && data.type in subs[data.coll]) {
      if ('id' in data && data.id in subs[data.coll][data.type])
        subs[data.coll][data.type][data.id].sub?.(data) ?? mcb(data);
      else subs[data.coll][data.type].sub?.(data) ?? mcb(data);
    } else subs[data.coll].sub?.(data) ?? mcb(data);
  });
}

async function closeSocket() {
  if (socket) socket.disconnect();
}

export async function Subscribe(e, callBack) {
  if (!('coll' in e)) return;
  if (!(e.coll in subs)) subs[e.coll] = {};
  if ('type' in e && !(e.type in subs[e.coll])) subs[e.coll][e.type] = {};
  if ('id' in e && !(e.id in subs[e.coll][e.type])) subs[e.coll][e.type][e.id] = {};
  socket.emit('sub', e);
  if ('type' in e)
    if ('id' in e) subs[e.coll][e.type][e.id].sub = callBack;
    else subs[e.coll][e.type].sub = callBack;
  else subs[e.coll].sub = callBack;
}

export async function Unsubscribe(e) {
  if (!('coll' in e)) return;
  socket.emit('unsub', e);
  if ('type' in e)
    if ('id' in e) delete subs[e.coll][e.type][e.id].sub;
    else delete subs[e.coll][e.type].sub;
  else delete subs[e.coll].sub;
}

// Auth
export async function Login(email, password) {
  await Logout();
  return dataPromise(
    apiFetch({ method: 'POST', endpoint: 'auth', data: { email, password } })
  ).then(response => {
    if (response.accessToken && response.refreshToken) {
      localStorage.setItem('token', (token = response.accessToken));
      initSocket();
    } else throw new Error('Unknown error');
  });
}

export async function Logout() {
  localStorage.removeItem('token');
  token = undefined;
  setAuthenticated(false);
  await closeSocket();
}

export function CheckLogin() { return !!token; }

export async function GetUser() {
  return dataPromise(apiFetch({ endpoint: 'users' }));
}

export async function ForgotPassword(email) {
  return dataPromise(apiFetch({ method: 'POST', endpoint: 'users/forgot', data: { email, app: 'lobby' } }));
}

export async function ResetPassword(data) {
  return dataPromise(apiFetch({ method: 'POST', endpoint: 'users/reset', data }));
}

// Queue
export async function GetQueue(orgId) {
  return dataPromise(apiFetch({ endpoint: 'lobby/queue', params: { org: orgId } }));
}

export async function TransitionVisit(visitId, orgId, data) {
  return dataPromise(apiFetch({ method: 'PATCH', endpoint: `lobby/visits/${visitId}/state`, data: { org: orgId, ...data } }));
}

export async function ReorderQueue(orgId, data) {
  return dataPromise(apiFetch({ method: 'POST', endpoint: 'lobby/queue/reorder', data: { org: orgId, ...data } }));
}

export async function SendSmsRecall(visitId, orgId) {
  return dataPromise(apiFetch({ method: 'POST', endpoint: `lobby/visits/${visitId}/messages`, data: { org: orgId } }));
}

// Manual entry / visits
export async function CreateVisit(data) {
  return dataPromise(apiFetch({ method: 'POST', endpoint: 'lobby/visits', data }));
}

export async function GetVisit(visitId, orgId) {
  return dataPromise(apiFetch({ endpoint: `lobby/visits/${visitId}`, params: { org: orgId } }));
}

// Configuration
export async function GetLobbyConfig(orgId) {
  return dataPromise(apiFetch({ endpoint: 'lobby/configuration', params: { org: orgId } }));
}

export async function UpdateLobbyConfig(orgId, data) {
  return dataPromise(apiFetch({ method: 'PATCH', endpoint: 'lobby/configuration', data: { org: orgId, ...data } }));
}

export async function GetReasons(orgId) {
  return dataPromise(apiFetch({ endpoint: 'lobby/reasons', params: { org: orgId } }));
}

export async function SaveReason(orgId, data) {
  return dataPromise(apiFetch({ method: 'POST', endpoint: 'lobby/reasons', data: { org: orgId, ...data } }));
}

export async function UpdateReason(reasonId, orgId, data) {
  return dataPromise(apiFetch({ method: 'PATCH', endpoint: `lobby/reasons/${reasonId}`, data: { org: orgId, ...data } }));
}

export async function DeleteReason(reasonId, orgId) {
  return dataPromise(apiFetch({ method: 'DELETE', endpoint: `lobby/reasons/${reasonId}`, params: { org: orgId } }));
}

export async function GetQuestions(orgId) {
  return dataPromise(apiFetch({ endpoint: 'lobby/questions', params: { org: orgId } }));
}

export async function SaveQuestion(orgId, data) {
  return dataPromise(apiFetch({ method: 'POST', endpoint: 'lobby/questions', data: { org: orgId, ...data } }));
}

export async function UpdateQuestion(questionId, orgId, data) {
  return dataPromise(apiFetch({ method: 'PATCH', endpoint: `lobby/questions/${questionId}`, data: { org: orgId, ...data } }));
}

export async function DeleteQuestion(questionId, orgId) {
  return dataPromise(apiFetch({ method: 'DELETE', endpoint: `lobby/questions/${questionId}`, params: { org: orgId } }));
}

// Users
export async function GetLobbyUsers(orgId) {
  return dataPromise(apiFetch({ endpoint: 'lobby/users', params: { org: orgId } }));
}

export async function CreateLobbyUser(data) {
  return dataPromise(apiFetch({ method: 'POST', endpoint: 'lobby/users', data }));
}

export async function UpdateLobbyUser(userId, orgId, data) {
  return dataPromise(apiFetch({ method: 'PATCH', endpoint: `lobby/users/${userId}`, data: { org: orgId, ...data } }));
}

export async function DeleteLobbyUser(userId, orgId) {
  return dataPromise(apiFetch({ method: 'DELETE', endpoint: `lobby/users/${userId}`, params: { org: orgId } }));
}

// Reports
export async function ReportSearch(orgId, params) {
  return dataPromise(apiFetch({ endpoint: 'lobby/reports/search', params: { org: orgId, ...params } }));
}

export async function ReportDaily(orgId, params) {
  return dataPromise(apiFetch({ endpoint: 'lobby/reports/daily', params: { org: orgId, ...params } }));
}

export async function ReportReasons(orgId, params) {
  return dataPromise(apiFetch({ endpoint: 'lobby/reports/reasons', params: { org: orgId, ...params } }));
}

export async function ReportStaff(orgId, params) {
  return dataPromise(apiFetch({ endpoint: 'lobby/reports/staff', params: { org: orgId, ...params } }));
}

export async function ReportWaitTimes(orgId, params) {
  return dataPromise(apiFetch({ endpoint: 'lobby/reports/wait-times', params: { org: orgId, ...params } }));
}

export async function ReportStatistics(orgId, params) {
  return dataPromise(apiFetch({ endpoint: 'lobby/reports/statistics', params: { org: orgId, ...params } }));
}

export async function ExportVisits(orgId, data) {
  return dataPromise(apiFetch({ method: 'POST', endpoint: 'lobby/exports', data: { org: orgId, ...data }, responseType: 'blob' }));
}

// Audit log
export async function GetAuditLog(orgId, params) {
  return dataPromise(apiFetch({ endpoint: 'lobby/events', params: { org: orgId, ...params } }));
}

// QR code
export async function GetQRCode(orgId) {
  return dataPromise(apiFetch({ endpoint: `lobby/qr/${orgId}` }));
}

// Kiosk devices
export async function GetKioskDevices(orgId) {
  return dataPromise(apiFetch({ endpoint: 'lobby/kiosk-devices', params: { org: orgId } }));
}

export async function ProvisionKioskDevice(orgId, name) {
  return dataPromise(apiFetch({ method: 'POST', endpoint: 'lobby/kiosk-devices', data: { org: orgId, name } }));
}

export async function RevokeKioskDevice(deviceId, orgId) {
  return dataPromise(apiFetch({ method: 'DELETE', endpoint: `lobby/kiosk-devices/${deviceId}`, params: { org: orgId } }));
}
